import {
	createContext,
	type FC,
	type PropsWithChildren,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";
import type { StepSettings } from "../../components/Stepper";
import type { StateSetter } from "../../helpers/types";

type OnboardContextType = {
	current: number;
	steps: Array<StepSettings>;
	currentStep: StepSettings;
	currentSubStep: string | undefined;
	setSteps: StateSetter<Array<StepSettings>>;
	updateStepStatus: (index: number, complete: boolean) => void;
	completeCurrentStep: () => void;
	prevStep: () => void;
	nextStep: () => void;
	prevSubstep: () => void;
	nextSubstep: () => void;
};

export const OnboardContext = createContext<OnboardContextType | null>(null);

interface OnboardProviderProps extends PropsWithChildren {
	steps: Array<StepSettings>;
}

export const OnboardProvider: FC<Readonly<OnboardProviderProps>> = ({
	children,
	steps: stepsArray,
}) => {
	const [current, setCurrent] = useState(0);
	const [steps, setSteps] = useState(stepsArray);

	const currentStep = useMemo(() => steps[current], [current, steps]);
	const currentSubStep = useMemo(
		() => currentStep?.substeps?.[currentStep.currentSubStep || 0],
		[currentStep],
	);

	const updateStepStatus = useCallback(
		(index: number, complete: boolean) => {
			const newSteps =
				steps.map((step, i) => {
					if (i === index) {
						return { ...step, complete };
					}
					return step;
				}) ?? [];
			setSteps(newSteps);
		},
		[steps],
	);

	const mutateStep = useCallback(
		(index: number, updatedStep: Partial<StepSettings>) => {
			setSteps(
				(steps) =>
					steps.map((step, i) => {
						if (i === index) {
							return { ...step, ...updatedStep };
						}
						return step;
					}) ?? [],
			);
		},
		[],
	);

	const prevSubstep = useCallback(() => {
		if (currentStep.substeps) {
			const { currentSubStep } = currentStep;

			if (currentSubStep === undefined) {
				return;
			}
			if (currentSubStep === 0) {
				return;
			}
			mutateStep(current, { currentSubStep: currentSubStep - 1 });
		}
	}, [current, currentStep, mutateStep]);

	const nextSubstep = useCallback(() => {
		if (currentStep.substeps) {
			const { currentSubStep } = currentStep;

			if (currentSubStep === undefined) {
				return;
			}
			if ((currentSubStep ?? 0) >= (currentStep.substeps ?? []).length - 1) {
				return;
			}
			mutateStep(current, { currentSubStep: currentSubStep + 1 });
		}
	}, [current, currentStep, mutateStep]);

	const prevStep = useCallback(() => {
		if (current === 0) {
			return;
		}

		setCurrent((prev) => prev - 1);

		const prevStep = steps[current - 1];
		if (prevStep.substeps) {
			mutateStep(current - 1, { currentSubStep: prevStep.substeps.length - 1 });
		}
	}, [current, steps, mutateStep]);

	const nextStep = useCallback(() => {
		if (current > steps.length - 1) {
			return;
		}

		setCurrent((prev) => prev + 1);

		const nextStep = steps[current + 1];
		if (nextStep.substeps) {
			mutateStep(current + 1, { currentSubStep: 0 });
		}
	}, [current, steps, mutateStep]);

	const completeCurrentStep = useCallback(() => {
		updateStepStatus(current, true);
	}, [current, updateStepStatus]);

	const contextValues = useMemo(
		() => ({
			current,
			currentStep,
			currentSubStep,
			steps,
			updateStepStatus,
			setSteps,
			completeCurrentStep,
			prevStep,
			nextStep,
			prevSubstep,
			nextSubstep,
		}),
		[
			current,
			steps,
			currentStep,
			currentSubStep,
			updateStepStatus,
			completeCurrentStep,
			prevStep,
			nextStep,
			prevSubstep,
			nextSubstep,
		],
	);

	return (
		<OnboardContext.Provider value={contextValues}>
			{children}
		</OnboardContext.Provider>
	);
};

export const useOnboardContext = () => {
	const context = useContext(OnboardContext);
	if (!context) {
		throw new Error("useOnboardContext must be used within a OnboardProvider");
	}
	return context;
};
